import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getAllProducts, getProductsByCollection, getNewArrivals } from '../services/productService';
import { getBrands } from '../services/brandService';
import { useMarket } from '../context/MarketContext';
import { useReviews } from '../context/ReviewsContext';
import ProductGrid from '../components/product/ProductGrid';
import { toHandle } from '../utils/handle';
import PriceFilter, { priceBounds } from '../components/shop/PriceFilter';
import { typeKey, priceOf, typeGroups, hasSkinTypes } from '../utils/catalog';

const SKIN_TYPES    = ['Oily', 'Dry', 'Combination', 'Sensitive', 'Normal', 'Acne-Prone'];
const RATINGS       = [4, 3, 2];
const SORT_OPTIONS  = [
  { label: 'Featured',          value: 'popularity' },
  { label: 'Price: Low → High', value: 'price_asc' },
  { label: 'Price: High → Low', value: 'price_desc' },
  { label: 'Newest First',      value: 'newest' },
  { label: 'Highest Discount',  value: 'discount' },
];
const ITEMS_PER_PAGE = 12;

const FilterGroup = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-0 py-3">
      <button
        className="flex items-center justify-between w-full text-left py-1"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-semibold text-ink">{title}</span>
        <svg className={`w-4 h-4 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
             fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      {open && <div className="mt-2.5">{children}</div>}
    </div>
  );
};

const FilterCheckbox = ({ checked, onChange, label, count }) => (
  <label className="flex items-center gap-2.5 cursor-pointer group py-1" onClick={onChange}>
    <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
      ${checked ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
      {checked && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
      )}
    </div>
    <span className="text-sm text-ink-soft group-hover:text-ink transition-colors flex-1">{label}</span>
    {count !== undefined && <span className="text-xs text-ink-muted">{count}</span>}
  </label>
);

const CollectionPage = () => {
  const { handle } = useParams();
  const topRef = useRef(null);
  const { currentMarket } = useMarket();
  const { ratingFor, anyRated } = useReviews();
  const marketCode = currentMarket?.isDefault ? null : currentMarket?.code;

  const [baseProducts,      setBaseProducts]      = useState([]);
  const [collectionMeta,    setCollectionMeta]     = useState(null);
  const [loading,           setLoading]            = useState(true);
  const [notFound,          setNotFound]           = useState(false);
  const [filterOpen,        setFilterOpen]         = useState(false);
  const [sortBy,            setSortBy]             = useState('popularity');
  const [page,              setPage]               = useState(1);
  const [showBackTop,       setShowBackTop]        = useState(false);

  // Sidebar filters
  const [selectedTypes,     setSelectedTypes]     = useState([]);
  const [selectedSkinTypes, setSelectedSkinTypes] = useState([]);
  const [priceLimit,        setPriceLimit]        = useState(null);   // null = any price
  const [minRating,         setMinRating]         = useState(0);

  // Reset filters and not-found state when handle changes
  useEffect(() => {
    setSelectedTypes([]);
    setSelectedSkinTypes([]);
    setPriceLimit(null);
    setMinRating(0);
    setSortBy('popularity');
    setPage(1);
    setNotFound(false);
    setCollectionMeta(null);
    setBaseProducts([]);
  }, [handle]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getAllProducts({ marketCode }),
      getBrands(),
    ]).then(([allProducts, allBrands]) => {
      if (cancelled) return;

      // 1 — New Arrivals: the most recently added products (the "new" flag is rarely set, which left this page empty)
      if (handle === 'new-arrivals') {
        setCollectionMeta({
          title: 'New Arrivals',
          description: 'Fresh launches — be the first to experience them',
          type: 'new-arrivals',
          breadcrumb: 'New Arrivals',
        });
        getNewArrivals(24, marketCode)
          .then(list => { if (!cancelled) setBaseProducts(list.length ? list : allProducts.slice(0, 24)); })
          .catch(() => { if (!cancelled) setBaseProducts(allProducts.slice(0, 24)); })
          .finally(() => { if (!cancelled) setLoading(false); });
        return;
      }

      // 2 — Brand slug (real API returns BrandDto with slug; mock data has slug too, but fall back
      //     to toHandle(name) in case a brand object lacks the field)
      const brand = allBrands.find(b => (b.slug || toHandle(b.name)) === handle);
      if (brand) {
        setCollectionMeta({
          title: brand.name,
          description: brand.description || `Shop all ${brand.name} products at Skinn Thrive.`,
          type: 'brand',
          breadcrumb: brand.name,
        });
        setBaseProducts(allProducts.filter(p => p.vendor === brand.name));
        setLoading(false);
        return;
      }

      // 3 — Product type slug, from the store's own types (e.g. face-serum → Face Serum)
      const matchedType = typeGroups(allProducts).find(g => toHandle(g.label) === handle);
      if (matchedType) {
        setCollectionMeta({
          title: matchedType.label,
          description: `Shop dermatologist-recommended ${matchedType.label} products at Skinn Thrive.`,
          type: 'productType',
          breadcrumb: matchedType.label,
        });
        setBaseProducts(allProducts.filter(p => typeKey(p) === matchedType.key));
        setLoading(false);
        return;
      }

      // 4 — Backend/mock collection fallback
      getProductsByCollection(handle, 200, marketCode).then(collectionProducts => {
        if (cancelled) return;
        if (!collectionProducts || collectionProducts.length === 0) {
          // Handle resolves to nothing — show not-found state
          setNotFound(true);
          setCollectionMeta(null);
          setBaseProducts([]);
          setLoading(false);
          return;
        }
        const title = handle
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        setCollectionMeta({
          title,
          description: `Shop ${title} products at Skinn Thrive.`,
          type: 'collection',
          breadcrumb: title,
        });
        setBaseProducts(collectionProducts);
        setLoading(false);
      }).catch(() => { if (!cancelled) setLoading(false); });
    }).catch(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [handle, marketCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fn = () => setShowBackTop(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const toggle = (value, list, setList) => {
    setList(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    setPage(1);
  };

  const groups = useMemo(() => typeGroups(baseProducts), [baseProducts]);
  const typeLabel = (key) => groups.find((g) => g.key === key)?.label ?? key;
  const { floor: priceFloor, ceiling: priceCeiling } = useMemo(() => priceBounds(baseProducts.map(priceOf)), [baseProducts]);
  const skinTypesKnown = useMemo(() => hasSkinTypes(baseProducts), [baseProducts]);

  const filtered = useMemo(() => {
    let r = [...baseProducts];
    if (selectedTypes.length)     r = r.filter(p => selectedTypes.includes(typeKey(p)));
    if (selectedSkinTypes.length) r = r.filter(p => p.skinType.some(s => selectedSkinTypes.includes(s)));
    if (priceLimit != null)       r = r.filter(p => priceOf(p) <= priceLimit);
    if (minRating > 0)            r = r.filter(p => (ratingFor(p.id)?.average ?? 0) >= minRating);
    switch (sortBy) {
      case 'price_asc':  return r.sort((a, b) => priceOf(a) - priceOf(b));
      case 'price_desc': return r.sort((a, b) => priceOf(b) - priceOf(a));
      // isNew is rarely set; a higher id is a more recently added product
      case 'newest':     return r.sort((a, b) => ((b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)) || (Number(b.id) - Number(a.id)));
      case 'discount':   return r.sort((a, b) => ((b.compareAtPrice || 0) - priceOf(b)) - ((a.compareAtPrice || 0) - priceOf(a)));
      // new arrivals keep the newest-first order the API gave them
      default:           return handle === 'new-arrivals' ? r : r.sort((a, b) => (b.isBestseller ? 1 : 0) - (a.isBestseller ? 1 : 0));
    }
  }, [baseProducts, selectedTypes, selectedSkinTypes, priceLimit, minRating, sortBy, ratingFor, handle]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated  = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const activeCount = selectedTypes.length + selectedSkinTypes.length + (minRating > 0 ? 1 : 0) + (priceLimit != null ? 1 : 0);

  const clearAll = () => {
    setSelectedTypes([]); setSelectedSkinTypes([]);
    setPriceLimit(null); setMinRating(0); setPage(1);
  };

  // Which filter groups to show: only choices that can find something
  const showTypeFilter     = collectionMeta?.type !== 'productType' && groups.length > 1;
  const showSkinTypeFilter = skinTypesKnown;

  // A plain element, not a component defined in here: a component made on every render is thrown away and rebuilt
  // each time the page updates, which froze the price slider mid-drag.
  const filterContent = (
    <div>
      {activeCount > 0 && (
        <button onClick={clearAll} className="w-full flex items-center justify-center gap-1.5 text-xs text-accent font-semibold py-2 px-3 bg-accent-light rounded-xl mb-4 hover:bg-accent hover:text-white transition-all">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Clear all filters ({activeCount})
        </button>
      )}

      <FilterGroup title="Price">
        <div className="px-1">
          <PriceFilter floor={priceFloor} ceiling={priceCeiling} limit={priceLimit}
            symbol={currentMarket?.currencySymbol ?? '₹'}
            onCommit={(val) => { setPriceLimit(val); setPage(1); }} />
        </div>
      </FilterGroup>

      {showTypeFilter && (
        <FilterGroup title="Product Type">
          <div className="space-y-0.5">
            {groups.map(g => (
              <FilterCheckbox key={g.key} checked={selectedTypes.includes(g.key)} onChange={() => toggle(g.key, selectedTypes, setSelectedTypes)} label={g.label} count={g.count} />
            ))}
          </div>
        </FilterGroup>
      )}

      {showSkinTypeFilter && (
        <FilterGroup title="Skin Type">
          <div className="flex flex-wrap gap-1.5">
            {SKIN_TYPES.map(st => (
              <button key={st} onClick={() => { toggle(st, selectedSkinTypes, setSelectedSkinTypes); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                  ${selectedSkinTypes.includes(st) ? 'bg-primary text-white border-primary' : 'border-gray-200 text-ink-soft hover:border-primary hover:text-primary'}`}>
                {st}
              </button>
            ))}
          </div>
        </FilterGroup>
      )}

      {anyRated && <FilterGroup title="Min. Rating" defaultOpen={false}>
        <div className="space-y-2">
          {RATINGS.map(r => (
            <label key={r} className="flex items-center gap-2.5 cursor-pointer">
              <input type="radio" checked={minRating === r} onChange={() => { setMinRating(r); setPage(1); }} className="accent-primary" />
              <div className="flex items-center gap-0.5">
                {Array.from({ length: r }).map((_, i) => <span key={i} className="text-amber-400 text-sm">★</span>)}
                <span className="text-xs text-ink-soft ml-1">& above</span>
              </div>
            </label>
          ))}
          {minRating > 0 && <button onClick={() => setMinRating(0)} className="text-xs text-accent hover:underline">Clear</button>}
        </div>
      </FilterGroup>}
    </div>
  );

  const meta = collectionMeta;
  const pageTitle = meta ? `${meta.title} — Skinn Thrive` : 'Collection — Skinn Thrive';
  const pageDescription = meta?.description || 'Shop authentic dermatologist-recommended skincare at Skinn Thrive.';

  // Not-found state — shown before the main layout so nothing else renders
  if (!loading && notFound) {
    return (
      <>
        <Helmet>
          <title>Collection Not Found — Skinn Thrive</title>
        </Helmet>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-surface flex items-center justify-center mx-auto mb-5">
            <svg className="w-9 h-9 text-ink-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Collection not found</h1>
          <p className="text-ink-soft mb-7">
            We couldn't find a collection for <span className="font-semibold text-ink">"{handle}"</span>.
            It may have been removed or the URL might be incorrect.
          </p>
          <Link to="/shop" className="btn-primary btn-lg">Browse All Products →</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/collections/${handle}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Skinn Thrive" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/collections/${handle}`} />
        {meta?.image && <meta property="og:image" content={meta.image} />}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {baseProducts.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: meta?.title || handle,
            url: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/collections/${handle}`,
            itemListElement: baseProducts.slice(0, 20).map((p, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/product/${p.handle}`,
              name: p.title,
            })),
          })}</script>
        )}
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={topRef}>
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-ink-muted mb-5">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span>›</span>
          <Link to="/brands" className="hover:text-primary transition-colors">Collections</Link>
          {meta && (
            <>
              <span>›</span>
              <span className="text-ink font-medium">{meta.breadcrumb}</span>
            </>
          )}
        </nav>

        {/* Page header */}
        <div className="mb-6">
          {loading ? (
            <div className="h-8 w-48 bg-gray-100 rounded-lg animate-pulse" />
          ) : (
            <>
              <h1 className="display-title text-3xl md:text-4xl">
                {meta?.title || 'Collection'}
              </h1>
              {meta?.description && (
                <p className="text-ink-soft text-sm mt-1">{meta.description}</p>
              )}
            </>
          )}
        </div>

        {/* Active filter chips */}
        {activeCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedTypes.map(t => (
              <span key={t} className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                {typeLabel(t)} <button onClick={() => toggle(t, selectedTypes, setSelectedTypes)} aria-label={`Remove ${typeLabel(t)}`}>✕</button>
              </span>
            ))}
            {priceLimit != null && (
              <span className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                Up to {currentMarket?.currencySymbol ?? '₹'}{priceLimit.toLocaleString('en-IN')}
                <button onClick={() => { setPriceLimit(null); setPage(1); }} aria-label="Remove price limit">✕</button>
              </span>
            )}
            {selectedSkinTypes.map(st => (
              <span key={st} className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                {st} Skin <button onClick={() => toggle(st, selectedSkinTypes, setSelectedSkinTypes)}>✕</button>
              </span>
            ))}
          </div>
        )}

        {/* Sticky results bar */}
        <div className="flex items-center justify-between gap-3 mb-5 py-3 border-b border-border sticky top-16 bg-white/95 backdrop-blur-sm z-10 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <p className="text-sm font-medium text-ink-soft whitespace-nowrap">
            <span className="font-bold text-ink">{loading ? '–' : filtered.length}</span> product{filtered.length !== 1 ? 's' : ''}<span className="hidden sm:inline"> found</span>
          </p>
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setFilterOpen(true)}
              className="md:hidden flex items-center gap-2 border border-border rounded-xl px-3.5 py-2 text-sm font-medium text-ink-soft hover:border-primary hover:text-primary transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
              </svg>
              Filters
              {activeCount > 0 && (
                <span className="w-5 h-5 text-[10px] font-bold bg-primary text-white rounded-full flex items-center justify-center">{activeCount}</span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setPage(1); }}
              className="min-w-0 border border-border rounded-xl px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-primary transition-all"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex gap-7">
          {/* Desktop sidebar */}
          <aside className="hidden md:block w-56 shrink-0">
            <div className="sticky top-32 bg-white rounded-2xl border border-border p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h2 className="text-xs font-bold uppercase tracking-widest text-ink-muted mb-4">Filter By</h2>
              {filterContent}
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="text-center py-16 text-gray-400">Loading products...</div>
            ) : baseProducts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg font-semibold text-ink mb-2">No products in this collection yet</p>
                <p className="text-ink-soft text-sm mb-6">Check back soon, or browse all our products.</p>
                <Link to="/shop" className="btn-primary">Browse All Products →</Link>
              </div>
            ) : (
              <>
                <ProductGrid
                  products={paginated}
                  emptyMessage="No products match your filters. Try removing some filters."
                />
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-10 flex-wrap">
                    <button
                      onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={page === 1}
                      className="px-3 py-2 text-sm font-medium disabled:opacity-40 text-ink-soft hover:text-primary transition-colors"
                    >
                      ← Prev
                    </button>
                    {(() => {
                      const pages = [];
                      for (let i = 1; i <= totalPages; i++) {
                        if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
                          pages.push(i);
                        } else if (pages[pages.length - 1] !== '…') {
                          pages.push('…');
                        }
                      }
                      return pages.map((p, idx) => p === '…' ? (
                        <span key={`e${idx}`} className="w-9 h-9 flex items-center justify-center text-ink-muted text-sm">…</span>
                      ) : (
                        <button key={p} onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all
                            ${p === page ? 'bg-primary text-white shadow-sm' : 'text-ink-soft hover:text-primary hover:bg-primary-light'}`}>
                          {p}
                        </button>
                      ));
                    })()}
                    <button
                      onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={page === totalPages}
                      className="px-3 py-2 text-sm font-medium disabled:opacity-40 text-ink-soft hover:text-primary transition-colors"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {filterOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm animate-fade-in" onClick={() => setFilterOpen(false)} />
          <div className="fixed inset-x-0 bottom-0 bg-white z-50 rounded-t-3xl max-h-[85vh] flex flex-col shadow-2xl animate-slide-up">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <h3 className="font-bold text-ink text-base">Filters {activeCount > 0 && <span className="text-primary">({activeCount})</span>}</h3>
              <button onClick={() => setFilterOpen(false)} className="w-8 h-8 rounded-xl bg-surface flex items-center justify-center text-ink-soft hover:bg-primary-light hover:text-primary transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4">
              {filterContent}
            </div>
            <div className="px-5 pb-6 pt-3 border-t border-border shrink-0">
              <button onClick={() => setFilterOpen(false)} className="btn-primary w-full py-3.5">
                Show {filtered.length} Product{filtered.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </>
      )}

      {showBackTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-11 h-11 bg-primary text-white rounded-full shadow-lg shadow-primary/30 flex items-center justify-center hover:bg-primary-dark hover:shadow-xl transition-all z-40 animate-fade-in"
          aria-label="Back to top"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7"/>
          </svg>
        </button>
      )}
    </>
  );
};

export default CollectionPage;
